import { useEffect, useState } from "react";
import { ModalHeader, ScrollableContent } from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import moment from "moment";
import DateRangePicker from "../Common/DateRangePicker";
import CustomTooltip from "../Tooltip";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import { CircularProgress } from "@mui/material";
import { FiAlertOctagon } from "react-icons/fi";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

// const bookingTrendData = {
//     labels: ["Feb", "Mar", "Apr", "May", "Jun", "Jul"],
//     datasets: [
//         {
//             label: "Bookings",
//             data: [38, 45, 52, 61, 68, 74],
//             borderColor: "#0078BD",
//             backgroundColor: "rgba(0,120,189,0.15)",
//             fill: false,
//             tension: 0.35,
//             pointRadius: 4,
//         },
//         {
//             label: "Moved In",
//             data: [30, 37, 43, 51, 59, 65],
//             borderColor: "#22C55E",
//             backgroundColor: "rgba(34,197,94,0.15)",
//             fill: false,
//             tension: 0.35,
//             pointRadius: 4,
//         },
//         {
//             label: "Cancelled",
//             data: [6, 5, 7, 4, 6, 5],
//             borderColor: "#EF4444",
//             backgroundColor: "rgba(239,68,68,0.15)",
//             fill: false,
//             tension: 0.35,
//             pointRadius: 4,
//         },
//     ],
// };

const transformBookingTrendData = (trendData) => {
    return {
        labels: trendData.map(item => item.month),
        datasets: [
            {
                label: "Bookings",
                data: trendData.map(item => item.bookings || 0),
                borderColor: "#0078BD",
                backgroundColor: "rgba(0,120,189,0.15)",
                fill: false,
                tension: 0.35,
                pointRadius: 4,
            },
            {
                label: "Moved In",
                data: trendData.map(item => item.movedIn || 0),
                borderColor: "#22C55E",
                backgroundColor: "rgba(34,197,94,0.15)",
                fill: false,
                tension: 0.35,
                pointRadius: 4,
            },
            {
                label: "Cancelled",
                data: trendData.map(item => item.cancelled || 0),
                borderColor: "#EF4444",
                backgroundColor: "rgba(239,68,68,0.15)",
                fill: false,
                tension: 0.35,
                pointRadius: 4,
            },
        ],
    };
};


const BookingsAnalyticsSideModal = ({ handleClose, token }) => {
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [apiResp, setApiResp] = useState(null);

    const [startDate, setStartDate] = useState(
        moment().startOf("month").format("YYYY-MM-DD")
    );

    const [endDate, setEndDate] = useState(
        moment().endOf("month").format("YYYY-MM-DD")
    );

    const fetchFunction = async () => {
        const response = await axios.get(
            `${Config.apiUrl}/bookings/analytics?startDate=${startDate}&endDate=${endDate}&term=1`,
            {
                headers: {
                    authorization: "Bearer" + " " + token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) error = true;
        else {
            setApiResp(res?.data);
        }
    };

    const getError = (res) => { };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        if (token && startDate && endDate) {
            getListMutate();
        }
    }, [token, startDate, endDate]);

    const bookingStats = data || {};

    const allProperties = Object.values(
        bookingStats?.summaryByProperty || {}
    ).sort((a, b) => b.totalBookings - a.totalBookings);

    const propertyList = allProperties.slice(0, 5);

    const allStaff = Object.values(
        bookingStats?.summaryByStaff || {}
    ).sort((a, b) => b.totalBookings - a.totalBookings);

    const staffList = allStaff.slice(0, 5);

    const categoryChartData = {
        labels: apiResp?.graphData?.map(c => c.category),
        datasets: [
            {
                data: apiResp?.graphData?.map(c => c.count),
                backgroundColor: [
                    "#6366F1", // Indigo
                    "#22C55E", // Green
                    "#F59E0B", // Amber
                    "#EF4444", // Red
                    "#06B6D4", // Cyan
                    "#8B5CF6", // Violet
                    "#EC4899", // Pink
                    "#14B8A6", // Teal
                    "#F97316", // Orange
                    "#84CC16"  // Lime
                ],
                borderWidth: 0,
            },
        ],
    };

    const pieOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
                // position: "bottom",
                // labels: { boxWidth: 12 },
            },
        },
    };

    const resolutionRate =
        apiResp?.stats?.totalComplaints > 0
            ? Math.round(
                (apiResp?.stats?.resolvedComplaints /
                    apiResp?.stats?.totalComplaints) *
                100
            )
            : 0;

    const propertyWise = bookingStats.propertyWise ?? [];
    const categoryWise = bookingStats.categoryWise ?? [];

    const maxProperty = Math.max(...propertyWise.map(p => p.count));
    const maxCategory = Math.max(...categoryWise.map(c => c.count));

    const submitHandler = (values) => {
        setStartDate(moment(values.startDate).format("YYYY-MM-DD"));
        setEndDate(moment(values.endDate).format("YYYY-MM-DD"));
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        plugins: {
            legend: {
                position: "top",
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    const bookingTrendData = transformBookingTrendData(bookingStats?.bookingTrend || []);
    return (
        <Container>
            <div className="flex items-center justify-between bg-white py-4 px-5 shadow-md bg-gradient-to-r from-primary-color to-[#00A0FF]">
                <div className="flex items-center gap-3 min-w-0">
                    <div
                        className="h-11 w-11 rounded-xl bg-[#EEF6FF] flex items-center justify-center shrink-0">
                        <div className="text-[#0078BD] text-lg">
                            <FiAlertOctagon />
                        </div>
                    </div>
                    <div className="min-w-0 text-white">
                        <h1 className="text-lg font-semibold text-white">
                            Bookings Analytics
                        </h1>
                        <DateLabel onClick={() => setShowDatePicker(true)}>
                            {moment(startDate).format("DD MMM yyyy")} – {moment(endDate).format("DD MMM yyyy")}
                        </DateLabel>
                    </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={handleClose}
                        className="h-10 w-10 rounded-xl hover:bg-primary-color/40 flex items-center justify-center transition-all duration-200 text-white">
                        <MdClose size={20} />
                    </button>
                </div>
            </div>

            {(isLoading || isRefetching) && (
                <div className="flex justify-center items-center my-40">
                    <CircularProgress size={40} />
                </div>
            )}
            {!isLoading && !isRefetching && (
                <div className="px-5">
                    <ScrollableContent className="overflow-y-auto max-h-[calc(100dvh-100px)] pb-10 mt-5">

                        <Content>
                            {/* KPI SECTION */}
                            <Grid className="grid-cols-3">
                                <KpiCard>
                                    <KpiTitle>Total Bookings</KpiTitle>
                                    <KpiValue>{bookingStats.totalBookings}</KpiValue>
                                    <KpiSub>Selected period</KpiSub>
                                </KpiCard>

                                <KpiCard>
                                    <KpiTitle>Moved-In</KpiTitle>
                                    <KpiValue>{bookingStats.movedInBookings}</KpiValue>
                                    <KpiSub>
                                        {bookingStats.totalBookings
                                            ? `${Math.round(
                                                (bookingStats.movedInBookings /
                                                    bookingStats.totalBookings) *
                                                100
                                            )}% of total`
                                            : "0%"}
                                    </KpiSub>
                                </KpiCard>

                                <KpiCard>
                                    <KpiTitle>Cancelled</KpiTitle>
                                    <KpiValue>{bookingStats.cancelledBookings}</KpiValue>
                                    <KpiSub>
                                        {bookingStats.totalBookings
                                            ? `${Math.round(
                                                (bookingStats.cancelledBookings /
                                                    bookingStats.totalBookings) *
                                                100
                                            )}% cancellation`
                                            : "0%"}
                                    </KpiSub>
                                </KpiCard>
                            </Grid>

                            {/* Graph */}
                            <Section>
                                <SectionHeader>
                                    <SectionTitle>Booking Trend (Last 6 Months)</SectionTitle>
                                </SectionHeader>
                                <div style={{ height: 300 }}>
                                    {/* {bookingTrendData.labels.length > 0 ? ( */}
                                    <Line
                                        data={bookingTrendData}
                                        options={lineOptions}
                                    />
                                    {/* ) : (
                                        <div className="flex items-center justify-center h-full text-gray-400">
                                            No trend data available
                                        </div>
                                    )} */}
                                </div>
                            </Section>

                            {/* PROPERTY ANALYTICS */}
                            <Section>
                                <SectionHeader>
                                    <SectionTitle>Properties with Most Bookings</SectionTitle>
                                </SectionHeader>

                                <List>
                                    {propertyList.length > 0 ? (
                                        propertyList.map((p) => (
                                            <Item key={p.id}>
                                                <ItemTop>
                                                    <PropertyName>{p.name}</PropertyName>
                                                    <Count>{p.totalBookings}</Count>
                                                </ItemTop>

                                                <ProgressTrack>
                                                    <ProgressBar
                                                        style={{
                                                            width: `${bookingStats.totalBookings
                                                                ? (p.totalBookings /
                                                                    bookingStats.totalBookings) *
                                                                100
                                                                : 0
                                                                }%`,
                                                        }}
                                                    />
                                                </ProgressTrack>

                                                <div className="flex items-center justify-between mt-3 text-xs">
                                                    <div className="flex gap-5">
                                                         <span className="text-green-600 font-medium">
                                                            ✓ Moved In: {p.movedInBookings || 0}
                                                        </span>
                                                        <span className="text-primary-color font-medium">
                                                            ✓ Confirmed: {p.confirmedBookings || 0}
                                                        </span>

                                                        <span className="text-red-600 font-medium">
                                                            ✕ Cancelled: {p.cancelledBookings || 0}
                                                        </span>
                                                    </div>

                                                    <span className="text-gray-500">
                                                        {bookingStats.totalBookings
                                                            ? `${Math.round(
                                                                (p.totalBookings /
                                                                    bookingStats.totalBookings) *
                                                                100
                                                            )}%`
                                                            : "0%"}
                                                    </span>
                                                </div>
                                            </Item>
                                        ))
                                    ) : (
                                        <EmptyState>
                                            No bookings reported for this period.
                                        </EmptyState>
                                    )}
                                </List>
                            </Section>

                            <Section>
                                <SectionHeader>
                                    <SectionTitle>Top Booking Staff</SectionTitle>
                                </SectionHeader>

                                <List>
                                    {staffList.length > 0 ? (
                                        staffList.map((staff) => (
                                            <Item key={staff.id}>
                                                <ItemTop>
                                                    <div>
                                                        <PropertyName>{staff.name}</PropertyName>

                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {staff.mobile}
                                                        </div>
                                                    </div>

                                                    <Count>{staff.totalBookings}</Count>
                                                </ItemTop>

                                                <ProgressTrack>
                                                    <ProgressBar
                                                        style={{
                                                            width: `${bookingStats.totalBookings
                                                                ? (staff.totalBookings /
                                                                    bookingStats.totalBookings) *
                                                                100
                                                                : 0
                                                                }%`,
                                                        }}
                                                    />
                                                </ProgressTrack>

                                                <div className="flex items-center justify-between mt-3 text-xs">
                                                    <div className="flex gap-5">
                                                        <span className="text-green-600 font-medium">
                                                            ✓ Bookings: {staff.totalBookings}
                                                        </span>

                                                        <span className="text-red-600 font-medium">
                                                            ✕ Cancelled: {staff.cancelledBookings}
                                                        </span>
                                                    </div>

                                                    <span className="text-gray-500">
                                                        {bookingStats.totalBookings
                                                            ? `${Math.round(
                                                                (staff.totalBookings /
                                                                    bookingStats.totalBookings) *
                                                                100
                                                            )}%`
                                                            : "0%"}
                                                    </span>
                                                </div>
                                            </Item>
                                        ))
                                    ) : (
                                        <EmptyState>
                                            No booking activity found.
                                        </EmptyState>
                                    )}
                                </List>
                            </Section>


                        </Content>
                    </ScrollableContent>

                    <DateRangePicker
                        show={showDatePicker}
                        onClose={() => setShowDatePicker(false)}
                        showOnlyMonths={true}
                        initialStartDate={new Date(startDate)}
                        initialEndDate={new Date(endDate)}
                        submitHandler={submitHandler}
                    />

                </div>
            )}
        </Container>
    );
};

/* ================= Styled ================= */

const Container = tw.div`
bg-gray-50 w-full h-full flex flex-col
`;

const Header = tw.div`
flex items-center justify-between bg-primary-color py-4 px-5 text-white
`;

const DateLabel = tw.span`text-xs opacity-80 cursor-pointer`;

const Content = tw.div`flex flex-col gap-5`;

const Grid = tw.div`
grid grid-cols-2 gap-4
`;

const KpiTitle = tw.p`
text-sm text-gray-500
`;

const KpiValue = tw.p`
text-2xl font-semibold text-gray-800 mt-1
`;

const KpiSub = tw.p`
text-xs text-gray-400 mt-1
`;

const ProgressWrapper = tw.div`
bg-gray-200 rounded-xl h-2 mt-3 overflow-hidden
`;

const ProgressBar = tw.div`
bg-green-500 h-2 rounded-xl
`;

const SectionTitle = tw.p`
text-sm font-semibold text-gray-700 mb-2
`;

const BarRow = tw.div`
flex items-center gap-3
`;

const Label = tw.span`
text-sm text-gray-600 w-40
`;

const BarWrapper = tw.div`
flex-1 bg-gray-200 rounded-xl h-2 overflow-hidden
`;

const Bar = tw.div`
bg-blue-500 h-2 rounded-xl
`;

const Count = tw.span`
text-sm font-semibold text-gray-700
`;

const TenantRow = tw.div`
flex justify-between text-sm text-gray-600
`;

const TenantCount = tw.span`
font-semibold text-gray-800
`;

const SectionHeader = tw.div`
flex items-center justify-between mb-3
`;

const ChartWrapper = tw.div`
h-[260px] flex items-center justify-center
`;

const TenantGrid = tw.div`
grid grid-cols-2 gap-3
`;

const TenantCard = tw.div`
bg-gray-50 hover:bg-white border rounded-xl p-4 
flex items-center justify-between 
transition shadow-sm hover:shadow-md
`;

const TenantInfo = tw.div`
flex flex-col
`;

const TenantName = tw.span`
text-sm font-semibold text-gray-800
`;

const TenantProperty = tw.span`
text-xs text-gray-500 mt-1
`;

const TenantBadge = tw.span`
bg-primary-color text-white text-sm font-semibold 
px-3 py-1 rounded-xl
`;

const KpiCard = tw.div`bg-white rounded-xl border p-6 shadow-sm`;

const Section = tw.div`bg-white rounded-xl border p-6 shadow-sm flex flex-col`;

// const Section = tw.div`
// bg-white rounded-xl border border-gray-200
// p-6 shadow-sm
// `;

// const SectionHeader = tw.div`
// flex items-center justify-between mb-5
// `;

// const SectionTitle = tw.p`
// text-base font-semibold text-gray-800
// `;

const List = tw.div`
flex flex-col gap-5
`;

const Item = tw.div`
flex flex-col gap-2
`;

const ItemTop = tw.div`
flex items-center justify-between
`;

const PropertyName = tw.span`
text-sm text-gray-700 font-medium
`;

// const Count = tw.span`
// text-sm font-semibold text-gray-900
// `;

const ProgressTrack = tw.div`
w-full h-2.5 rounded-xl bg-gray-100 overflow-hidden
`;

const EmptyState = tw.div`
text-sm text-gray-400 text-center py-6
`;

const CategoryList = tw.div`
flex flex-col gap-3 mt-4
`;

const CategoryItem = tw.div`
flex items-center justify-between text-sm
`;

const CategoryLeft = tw.div`
flex items-center gap-2
`;

const ColorDot = tw.div`
w-3 h-3 rounded-sm
`;

const CategoryName = tw.span`
text-gray-600
`;

const CategoryRight = tw.div`
flex items-center gap-3
`;

const CategoryCount = tw.span`
font-semibold text-gray-800
`;

const CategoryPercent = tw.span`
text-gray-400 text-xs
`;

// const ProgressBar = tw.div`
// h-full rounded-xl
// bg-gradient-to-r from-blue-500 to-blue-600
// transition-all duration-500
// `;
export default BookingsAnalyticsSideModal;